import React from 'react';
import { motion } from 'framer-motion';
import { Bell, BellOff, Circle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '../../../providers/TranslationProvider';

const Step5Updates = ({ data, onUpdate, isDarkMode }) => {
    const { t } = useTranslation();
    const notificationsEnabled = data.notificationsEnabled;

    const options = [
        {
            value: false,
            label: t('onboarding.updates.no_short'),
            desc: t('onboarding.updates.no_desc'),
            icon: BellOff
        },
        {
            value: true,
            label: t('onboarding.updates.yes_short'),
            desc: t('onboarding.updates.yes_desc'),
            icon: Bell
        },
    ];

    return (
        <div className="w-full space-y-4">
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className={`p-6 rounded-2xl flex items-center justify-center relative overflow-hidden backdrop-blur-md ${isDarkMode
                    ? 'bg-blue-600/10 border border-blue-500/20'
                    : 'bg-blue-50 border border-blue-200'
                    }`}
            >
                <div className="absolute inset-0 bg-blue-500/5 blur-3xl rounded-full scale-150 animate-pulse"></div>
                <div className="flex gap-4 relative z-10">
                    <motion.div
                        animate={{ rotate: [0, 15, -15, 0] }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    >
                        <Bell size={48} className={isDarkMode ? 'text-blue-400 drop-shadow-[0_0_15px_rgba(96,165,250,0.5)]' : 'text-blue-600'} />
                    </motion.div>
                </div>
            </motion.div>

            <motion.div
                className="grid grid-cols-1 gap-2.5"
                initial="hidden"
                animate="visible"
                variants={{
                    visible: { transition: { staggerChildren: 0.1 } }
                }}
            >
                {options.map((option) => {
                    const isSelected = notificationsEnabled === option.value;
                    return (
                        <motion.button
                            key={option.value.toString()}
                            variants={{
                                hidden: { opacity: 0, y: 10 },
                                visible: { opacity: 1, y: 0 }
                            }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => onUpdate({ notificationsEnabled: option.value })}
                            className={`w-full p-4 rounded-xl border flex items-center gap-3 text-left transition-all duration-200 backdrop-blur-md ${isSelected
                                ? 'border-blue-500 bg-blue-500/10'
                                : isDarkMode
                                    ? 'border-white/5 bg-white/5 hover:border-white/20'
                                    : 'border-slate-200 bg-white/50 hover:border-slate-300'
                                }`}
                        >
                            <div className="mt-0.5 shrink-0">
                                {isSelected
                                    ? <CheckCircle2 className="text-blue-500 drop-shadow-md" size={22} />
                                    : <Circle className={isDarkMode ? 'text-slate-500' : 'text-slate-300'} size={22} />
                                }
                            </div>
                            <div className="flex flex-col">
                                <span className={`text-base font-semibold leading-none ${isSelected ? 'text-blue-400' : isDarkMode ? 'text-slate-200' : 'text-slate-800'
                                    }`}>
                                    {option.label}
                                </span>
                                <span className={`text-xs mt-1 leading-none ${isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                    }`}>
                                    {option.desc}
                                </span>
                            </div>
                        </motion.button>
                    );
                })}
            </motion.div>
        </div>
    );
};

export default Step5Updates;
